/**
 * PoliteStatus — shared polite live-region for async status messages.
 *
 * Phase L.2 P2 batch fix for the systemic pattern from audit §5.4:
 * `Loader2` icons + loading strings + async result confirmations all
 * render as static text with no assistive-tech announcement. This
 * component wraps them in a `role="status"` + `aria-live="polite"`
 * region that screen readers announce on message change.
 *
 * Contract:
 *   - `<PoliteStatus message="Sending…" />` renders a visible
 *     `<p role="status" aria-live="polite" aria-atomic="true">`.
 *   - `<PoliteStatus message="" />` (or `null` / `undefined`) STILL
 *     renders the region — just empty. This is intentional: the
 *     assistive-tech announcement fires on message CHANGE, so the
 *     region must be in the DOM continuously for the transition
 *     from "" → "Loaded" to be announced. Toggling the region
 *     itself off the tree misses the announcement in most SR.
 *   - `visuallyHidden={true}` wraps the message in an `sr-only`
 *     span so the visible UI is unaffected but SR still gets it.
 *
 * Consumer patterns:
 *   1. Visible loading text — leave `visuallyHidden` unset:
 *        `<PoliteStatus message={loading ? "Loading coverage…" : ""} />`
 *   2. Loading state where the visible UI is already a spinner and
 *      the polite announcement is the a11y-only complement:
 *        `<PoliteStatus visuallyHidden message={loading ? "Loading…" : ""} />`
 *   3. Async success confirmation (after form submit):
 *        `<PoliteStatus message="Message sent" />`
 *
 * `className` prop lets callers override the visible styling to fit
 * the surrounding surface.
 *
 * Family L discipline: no inline hex, no `prefers-color-scheme` in
 * this file. Consumers pass Tailwind utility strings for styling.
 */
import React from "react";

export default function PoliteStatus({
  message,
  visuallyHidden = false,
  className = "",
  testId,
}) {
  const safeMessage = message ?? "";
  const commonProps = {
    role: "status",
    "aria-live": "polite",
    "aria-atomic": "true",
    ...(testId ? { "data-testid": testId } : {}),
  };
  if (visuallyHidden) {
    return (
      <p className={`sr-only ${className}`.trim()} {...commonProps}>
        {safeMessage}
      </p>
    );
  }
  return (
    <p className={className} {...commonProps}>
      {safeMessage}
    </p>
  );
}
